import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Mountain, LayoutDashboard, User } from "lucide-react";
import UploadDialog from "./UploadDialog";

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  background: #f9fafb;
  font-family: "Inter", sans-serif;

  @media (max-width: 800px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
`;

const Sidebar = styled.div`
  width: 220px;
  background: #fff;
  color: #000;
  display: flex;
  flex-direction: column;
  padding: 24px 0 0 0;
  font-family: "Inter", sans-serif;
  box-shadow: 0 0px 23px rgba(0, 0, 0, 0.05);
  @media (max-width: 800px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const SidebarLogo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  padding: 0 0 32px 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 800px) {
    padding: 0;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 32px;
  @media (max-width: 800px) {
    display: none;
  }
`;

const SidebarNavItem = styled.div`
  font-size: 1rem;
  color: #000;
  padding: 8px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  &:hover {
    color: rgb(86, 86, 86);
  }
`;

const Main = styled.div`
  flex: 1;
  padding: 40px 40px 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  box-sizing: border-box;

  @media (max-width: 800px) {
    padding: 20px 20px 0 20px;
    height: auto;
  }
`;

const TopBar = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 32px;
  gap: 12px;
  position: sticky;
  top: 0;
  background: #f9fafb;
  z-index: 2;
`;

const RegionSelect = styled.select`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #333;
  font-size: 1rem;
`;

const UploadButton = styled.button`
  background: #23272e;
  color: #fff;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #111318;
  }
`;

const ResortGrid = styled.div`
  width: 100%;
  max-width: 1200px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ResortCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  padding: 0;
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    border 0.15s;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #b6c0d1;
  }
`;

const ResortCardTop = styled.div`
  width: 100%;
  height: 150px;
  background: #f3f4f6;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url("/lines.png") center center / cover no-repeat;
    opacity: 0.8;
    z-index: 0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const ResortLogo = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ResortInfo = styled.div`
  padding: 18px 24px 20px 24px;
`;

const ResortName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #23272e;
`;

const ResortLocation = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 1px;
`;

const ResortVisits = styled.div`
  position: absolute;
  top: 16px;
  right: 18px;
  background: #fff;
  color: #23272e;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 4px 14px;
`;

const ResortScrollArea = styled.div`
  width: 100%;
  max-width: 1200px;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 15px;
  margin-right: -15px;
  padding-bottom: 40px;
`;

// Dummy region list for now
const regions = ["All", "California", "Colorado", "Utah"];

// Demo data for empty state
const demoResort = {
  id: "recnxD2PLO8gLpNei",
  name: "Mammoth Mountain",
  location: "Mammoth Lakes, CA",
  totalVisits: 0,
  logo: "mammoth.svg",
  isDemo: true,
};

export default function Home({
  resorts = [],
  resortsLoaded = false,
  fetchResorts,
}) {
  const navigate = useNavigate();
  const [region, setRegion] = useState("All");
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Filter resorts by region if needed
  let filteredResorts = resorts.filter(
    (resort) => region === "All" || resort.location === region
  );
  // Sort by number of visits (totalVisits), descending
  filteredResorts = filteredResorts.sort((a, b) => {
    const visitsA = a.totalVisits || 0;
    const visitsB = b.totalVisits || 0;
    return visitsB - visitsA;
  });

  // Filter out resorts with no visits
  filteredResorts = filteredResorts.filter((resort) => resort.totalVisits > 0);

  // Only show demo tile after resorts have loaded and no other resorts were returned
  const resortsToDisplay =
    filteredResorts.length > 0
      ? filteredResorts
      : resortsLoaded
        ? [demoResort]
        : [];

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogo>
          <Mountain size={28} style={{ marginRight: -7, marginBottom: 5 }} />
          LastRun
        </SidebarLogo>
        <SidebarNav>
          <SidebarNavItem>
            <LayoutDashboard size={18} style={{ marginRight: 8 }} />
            Dashboard
          </SidebarNavItem>
          <SidebarNavItem>
            <User size={18} style={{ marginRight: 8 }} />
            Account
          </SidebarNavItem>
        </SidebarNav>
      </Sidebar>
      <Main>
        <TopBar>
          <RegionSelect
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </RegionSelect>
          <UploadButton onClick={() => setShowUploadDialog(true)}>
            Upload GPX Files
          </UploadButton>
        </TopBar>
        <ResortScrollArea>
          <ResortGrid>
            {resortsToDisplay.map((resort) => (
              <ResortCard
                key={resort.id}
                onClick={() => {
                  navigate(`/app/resort/${resort.id}`, {
                    state: { isDemo: !!resort.isDemo },
                  });
                }}
              >
                <ResortCardTop>
                  <ResortLogo>
                    {resort.logo ? (
                      <img src={`/${resort.logo}`} alt={resort.name} />
                    ) : (
                      <Mountain size={48} color="#bbb" />
                    )}
                  </ResortLogo>
                </ResortCardTop>
                <ResortVisits>
                  {resort.isDemo ? "Demo" : `${resort.totalVisits || 0} visits`}
                </ResortVisits>
                <ResortInfo>
                  <ResortName>{resort.name}</ResortName>
                  <ResortLocation>{resort.location || "\u200B"}</ResortLocation>
                </ResortInfo>
              </ResortCard>
            ))}
          </ResortGrid>
        </ResortScrollArea>
        {showUploadDialog && (
          <UploadDialog
            resorts={resorts}
            onClose={() => setShowUploadDialog(false)}
            onUpload={fetchResorts}
          />
        )}
      </Main>
    </PageContainer>
  );
}
